import { Trans } from '@lingui/react/macro';

import Badge from '@/design_system/Badge';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconStar from '@/icons/Star.svg';
import { useBrandToWorkshops } from '@/models/brandToWorkshop';

const ACTIVE_WARRANTY_TYPE_LABELS = {
  supplier: <Trans id="brand-to-workshop.warranty-type.supplier">Supplier</Trans>,
  distributor: <Trans id="brand-to-workshop.warranty-type.distributor">Distributor</Trans>,
  override: <Trans id="brand-to-workshop.warranty-type.override">Override</Trans>,
  noWarranty: <Trans id="brand-to-workshop.warranty-type.no-warranty">No warranty</Trans>,
} as const;

export const BrandToWorkshop = ({ workshopId }: { workshopId: string }) => {
  const { data } = useBrandToWorkshops(workshopId);

  if (!data || data.brandToWorkshops.length === 0) {
    return null;
  }

  return (
    <Stack gap="1em">
      <Title as="h3" size="300">
        <Trans id="settings.entities.brand-to-workshop.label">Brands</Trans>
      </Title>
      <Table ariaLabel="brand-to-workshop" columnWidths={['1fr', '1fr']} bordered>
        <Header>
          <Row>
            <Column>
              <Trans id="settings.entities.brand-to-workshop.table.column.brand">Brand</Trans>
            </Column>
            <Column>
              <Trans id="settings.entities.brand-to-workshop.table.column.warranty-types">
                Warranty Types
              </Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {data?.brandToWorkshops.map((row) => (
            <Row key={row.brandName}>
              <Cell>
                <Stack gap=".5em" row>
                  <Paragraph weight="medium">{row.brandName}</Paragraph>
                  {row.official && (
                    <Badge size="small" color="warning" borderRadius="soft" hasBorder>
                      <IconStar filled />
                      <Trans id="components.workshop-list.official">Official</Trans>
                    </Badge>
                  )}
                </Stack>
              </Cell>
              <Cell>
                <Stack gap=".5em" row>
                  {row.warrantyTypes.length > 0
                    ? row.warrantyTypes.map((type) => (
                        <Badge
                          key={type}
                          size="small"
                          color="neutral"
                          borderRadius="soft"
                          hasBorder
                        >
                          {ACTIVE_WARRANTY_TYPE_LABELS[type]}
                        </Badge>
                      ))
                    : '-'}
                </Stack>
              </Cell>
            </Row>
          ))}
        </Body>
      </Table>
    </Stack>
  );
};
